import React, { useEffect, useState } from 'react';
import Navbar from '../../components/Navbar';
import { Link } from 'react-router-dom';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ShareExamModal from '../../components/ShareExamModal';

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function generateQuestionPaperPDFs(exam, questions, branding) {
  const sets = ['A', 'B', 'C', 'D'];
  const brandName = branding?.teacher_name || 'Teacher';
  const phone = branding?.phone || '';
  const logoPath = branding?.logo_path || null;

  sets.forEach(setLabel => {
    // Shuffle questions and options for each set
    const shuffledQs = shuffleArray(questions).map((q, qi) => {
      const optionObjs = q.options.map((opt, oi) => ({
        text: opt,
        isCorrect: q.correct_answers.includes(opt),
        origLabel: String.fromCharCode(65 + oi),
      }));
      const shuffledOpts = shuffleArray(optionObjs);
      const correctLabels = shuffledOpts
        .map((o, i) => o.isCorrect ? String.fromCharCode(65 + i) : null)
        .filter(Boolean);
      return { ...q, shuffledOpts, correctLabels, setOrder: qi + 1 };
    });

    // ── Question Paper PDF ──
    const doc = new jsPDF();
    const pw = doc.internal.pageSize.getWidth();

    // Header
    doc.setFillColor(30, 30, 60);
    doc.rect(0, 0, pw, 32, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16); doc.setFont('helvetica', 'bold');
    doc.text(brandName, pw / 2, 12, { align: 'center' });
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    if (phone) doc.text(`📞 ${phone}`, pw / 2, 19, { align: 'center' });
    doc.setFontSize(13); doc.setFont('helvetica', 'bold');
    doc.text(`${exam.title}  |  Set ${setLabel}`, pw / 2, 27, { align: 'center' });

    doc.setTextColor(0, 0, 0);
    let y = 42;

    // Exam info row
    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    const infoParts = [];
    if (exam.subject) infoParts.push(`Subject: ${exam.subject}`);
    infoParts.push(`Duration: ${exam.duration_minutes} min`);
    infoParts.push(`Total Marks: ${shuffledQs.reduce((a, q) => a + (q.marks || 1), 0)}`);
    infoParts.push(`Questions: ${shuffledQs.length}`);
    doc.text(infoParts.join('   |   '), 14, y);
    y += 5;

    // Student info boxes
    doc.setDrawColor(180, 180, 180);
    doc.setFontSize(9);
    doc.text('Name: ___________________________', 14, y + 7);
    doc.text('Roll No: ___________', 110, y + 7);
    doc.text('Date: _______________', 14, y + 14);
    doc.text('Sign: _______________', 110, y + 14);
    y += 22;

    doc.setDrawColor(200, 200, 200);
    doc.line(14, y, pw - 14, y);
    y += 6;

    // Questions
    doc.setTextColor(0, 0, 0);
    shuffledQs.forEach((q, qi) => {
      const marksLabel = `[${q.marks || 1}m]`;
      const qLines = doc.splitTextToSize(
        `Q${qi + 1}. ${q.question_text.replace(/\$[^$]+\$/g, '[formula]')}  ${marksLabel}`,
        pw - 28
      );

      // page break check
      if (y + qLines.length * 5 + 24 > doc.internal.pageSize.getHeight() - 20) {
        doc.addPage(); y = 20;
      }

      doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
      doc.text(qLines, 14, y);
      y += qLines.length * 5 + 2;

      doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
      q.shuffledOpts.forEach((opt, oi) => {
        const optLabel = `  ${String.fromCharCode(65 + oi)}) ${opt.text.replace(/\$[^$]+\$/g, '[formula]')}`;
        const optLines = doc.splitTextToSize(optLabel, (pw - 28) / 2);
        if (oi % 2 === 0) {
          if (y + optLines.length * 5 > doc.internal.pageSize.getHeight() - 20) {
            doc.addPage(); y = 20;
          }
          doc.text(optLines, 18, y);
        } else {
          doc.text(optLines, pw / 2 + 4, y - optLines.length * 5);
          y += Math.max(optLines.length, 1) * 5;
        }
      });
      if (q.shuffledOpts.length % 2 !== 0) y += 5;
      y += 4;

      // Dotted separator
      doc.setDrawColor(220, 220, 220);
      doc.setLineDashPattern([1, 2], 0);
      doc.line(14, y, pw - 14, y);
      doc.setLineDashPattern([], 0);
      y += 4;
    });

    // Watermark
    doc.setTextColor(200, 200, 200); doc.setFontSize(40);
    doc.setFont('helvetica', 'bold');
    for (let pg = 1; pg <= doc.internal.getNumberOfPages(); pg++) {
      doc.setPage(pg);
      doc.saveGraphicsState?.();
      doc.text(`Set ${setLabel}`, pw / 2, doc.internal.pageSize.getHeight() / 2, {
        align: 'center', angle: 45,
      });
    }

    doc.save(`${exam.title}_Set${setLabel}_QuestionPaper.pdf`);

    // ── Answer Key PDF ──
    const keyDoc = new jsPDF();
    keyDoc.setFillColor(30, 30, 60);
    keyDoc.rect(0, 0, pw, 24, 'F');
    keyDoc.setTextColor(255, 255, 255);
    keyDoc.setFontSize(13); keyDoc.setFont('helvetica', 'bold');
    keyDoc.text(`${exam.title} — Set ${setLabel} — ANSWER KEY`, pw / 2, 15, { align: 'center' });
    keyDoc.setTextColor(0, 0, 0);

    autoTable(keyDoc, {
      startY: 30,
      head: [['Q.No', 'Correct Answer(s)', 'Marks']],
      body: shuffledQs.map((q, qi) => [
        `Q${qi + 1}`,
        q.correctLabels.join(', '),
        q.marks || 1,
      ]),
      styles: { fontSize: 10 },
      headStyles: { fillColor: [99, 102, 241] },
      columnStyles: { 0: { cellWidth: 18 }, 2: { cellWidth: 18 } },
    });

    const ky = keyDoc.lastAutoTable.finalY + 10;
    keyDoc.setFontSize(9); keyDoc.setTextColor(130, 130, 130);
    keyDoc.text(`Generated by ${brandName} • Confidential`, pw / 2, ky, { align: 'center' });

    keyDoc.save(`${exam.title}_Set${setLabel}_AnswerKey.pdf`);
  });

  toast.success('✅ 8 PDFs downloaded! (4 question papers + 4 answer keys)');
}

export default function TeacherExams() {
  const [exams, setExams] = useState([]);
  const [liveStats, setLiveStats] = useState({});
  const [generatingPdf, setGeneratingPdf] = useState(null);
  const [branding, setBranding] = useState(null);
  const [shareExam, setShareExam] = useState(null);

  const load = () => API.get('/exams').then(r => setExams(r.data));
  useEffect(() => {
    load();
    API.get('/auth/me').then(r => setBranding({
      teacher_name: r.data.full_name,
      phone: r.data.teacher_phone,
      logo_path: r.data.teacher_logo,
    })).catch(() => {});
  }, []);

  useEffect(() => {
    const published = exams.filter(e => e.is_published);
    if (published.length === 0) return;
    const fetchLive = () => {
      published.forEach(e => {
        API.get(`/admin/live-stats/${e.id}`).then(r => setLiveStats(s => ({ ...s, [e.id]: r.data }))).catch(()=>{});
      });
    };
    fetchLive();
    const interval = setInterval(fetchLive, 10000);
    return () => clearInterval(interval);
  }, [exams]);

  const del = async id => {
    if (!window.confirm('Delete this exam and all its data?')) return;
    await API.delete(`/exams/${id}`);
    toast.success('Deleted'); load();
  };

  const publish = async id => {
    try { await API.post(`/exams/${id}/publish`); toast.success('✅ Published!'); load(); }
    catch (err) { toast.error(err.response?.data?.error||'Error'); }
  };

  const unpublish = async id => {
    if (!window.confirm('This will immediately make the exam unavailable to everyone, including students currently taking it (their attempt may not be saved). Continue?')) return;
    try { await API.post(`/exams/${id}/unpublish`); toast.success('🔌 Exam taken offline'); load(); }
    catch (err) { toast.error(err.response?.data?.error||'Error'); }
  };

  const duplicate = async id => {
    try {
      await API.post(`/exams/${id}/duplicate`);
      toast.success('✅ Exam duplicated!'); load();
    } catch (err) { toast.error(err.response?.data?.error||'Error'); }
  };

  const handleGeneratePaper = async (exam) => {
    if (exam.question_count === 0) return toast.error('Add questions first!');
    setGeneratingPdf(exam.id);
    try {
      const r = await API.get(`/exams/${exam.id}`);
      const questions = r.data.questions;
      if (!questions || questions.length === 0) return toast.error('No questions found.');
      generateQuestionPaperPDFs(exam, questions, branding);
    } catch (err) {
      toast.error('Failed to load questions');
    } finally {
      setGeneratingPdf(null);
    }
  };

  return (
    <div>
      <Navbar />
      <div style={{ maxWidth:1200, margin:'0 auto', padding:'32px 20px' }}>
        <div className="page-header flex-between">
          <div><h1>📝 My Exams</h1><p>Create and manage your exams</p></div>
          <Link to="/teacher/exams/new" className="btn btn-primary">➕ New Exam</Link>
        </div>

        {exams.length === 0 ? (
          <div className="card text-center" style={{ padding:'60px 20px' }}>
            <div style={{ fontSize:64, marginBottom:16 }}>📝</div>
            <h3>No exams yet</h3>
            <p className="text-muted" style={{ margin:'8px 0 24px' }}>Create your first exam and share with students</p>
            <Link to="/teacher/exams/new" className="btn btn-primary btn-lg">➕ Create First Exam</Link>
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            {exams.map(e => (
              <div key={e.id} className="card" style={!e.is_published && e.credit_consumed ? { opacity:0.7, borderColor:'var(--danger)' } : undefined}>
                <div className="flex-between" style={{ flexWrap:'wrap', gap:12 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginBottom:6 }}>
                      <h3 style={{ fontWeight:700, fontSize:18 }}>{e.title}</h3>
                      {e.is_published ? (
                        <span className="badge badge-success">🟢 Live</span>
                      ) : e.credit_consumed ? (
                        <span className="badge badge-danger">🔌 Offline</span>
                      ) : (
                        <span className="badge badge-warning">📋 Draft</span>
                      )}
                      {e.is_published && liveStats[e.id] && liveStats[e.id].active > 0 && (
                        <span className="badge badge-danger" style={{ animation:'pulse 1.5s infinite' }}>🔴 {liveStats[e.id].active} live</span>
                      )}
                    </div>
                    <div style={{ display:'flex', gap:12, flexWrap:'wrap', fontSize:13, color:'var(--text2)' }}>
                      {e.subject && <span>📚 {e.subject}</span>}
                      <span>❓ {e.question_count} questions</span>
                      <span>✍️ {e.attempt_count} submitted</span>
                      <span>⏱️ {e.duration_minutes} min</span>
                      {e.negative_marking ? <span style={{ color:'#f87171' }}>➖ Negative marking</span> : null}
                      {e.exam_password ? <span>🔒 Password protected</span> : null}
                    </div>
                    {e.is_published && e.link_token && (
                      <div style={{ marginTop:8, background:'var(--card2)', borderRadius:8, padding:'6px 12px', fontSize:12, fontFamily:'var(--mono)', color:'var(--text2)', wordBreak:'break-all' }}>
                        🔗 {window.location.origin}/#/exam/{e.link_token}
                      </div>
                    )}
                  </div>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'flex-start' }}>
                    <Link to={`/teacher/exams/${e.id}/edit`} className="btn btn-secondary btn-sm">✏️ Edit</Link>
                    <Link to={`/teacher/exams/${e.id}/results`} className="btn btn-secondary btn-sm">📊 Results</Link>
                    <button onClick={() => duplicate(e.id)} className="btn btn-secondary btn-sm">📋 Copy</button>
                    {e.question_count > 0 && (
                      <button
                        onClick={() => handleGeneratePaper(e)}
                        className="btn btn-secondary btn-sm"
                        disabled={generatingPdf === e.id}
                        title="Generate 4 shuffled question paper sets (A/B/C/D) with answer keys"
                        style={{ background:'rgba(139,92,246,0.15)', color:'#a78bfa', borderColor:'rgba(139,92,246,0.4)' }}
                      >
                        {generatingPdf === e.id ? '⏳...' : '📄 Paper Sets'}
                      </button>
                    )}
                    {!e.is_published && !e.credit_consumed && e.question_count > 0 && <button onClick={() => publish(e.id)} className="btn btn-success btn-sm">🚀 Publish</button>}
                    {!e.is_published && e.credit_consumed && <button onClick={() => publish(e.id)} className="btn btn-success btn-sm">🔁 Republish</button>}
                    {e.is_published && e.link_token && (
                      <button onClick={() => setShareExam(e)} className="btn btn-primary btn-sm">🔗 Share</button>
                    )}
                    {e.is_published && (
                      <button onClick={() => unpublish(e.id)} className="btn btn-danger btn-sm">🔌 Unpublish</button>
                    )}
                    <button onClick={() => del(e.id)} className="btn btn-danger btn-sm">🗑️</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }`}</style>
      {shareExam && <ShareExamModal exam={shareExam} onClose={() => setShareExam(null)} />}
    </div>
  );
}
